import React, { useContext, useEffect, useState } from "react";
import Header from "./Header";
import Dreams from "./Dreams";
import Planing from "./Planing";
import Goals from "./Goals";
import Feedback from "./Feedback";
import JoinFrond from "./JoinFrond";
import { Card, CardBody, Image, Link, Spinner } from "@nextui-org/react";
import axios from "axios";

const Homepage = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLatestEvents();
  }, []);

  // Helper function to get random events that change daily
  const getDailyRandomEvents = (eventsData, count = 3) => {
    if (!eventsData || eventsData.length === 0) return [];
    
    // Create a seed based on the current date (changes at midnight)
    const today = new Date();
    const dateSeed = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    
    // Simple seeded random function
    const seededRandom = (seed) => {
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(Math.sin(hash)) % 1;
    };
    
    // Filter published events
    const publishedEvents = eventsData.filter(event => event.is_published === true);
    
    if (publishedEvents.length <= count) {
      return publishedEvents;
    }
    
    // Create a copy and sort with seeded random
    const shuffled = [...publishedEvents];
    const random = seededRandom(dateSeed);
    
    // Fisher-Yates shuffle with seeded random
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom(dateSeed + i) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Return the first 'count' items
    return shuffled.slice(0, count);
  };

  const fetchLatestEvents = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      const response = await axios({
        method: 'GET',
        url: '/blogs',
        headers: {
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        timeout: 10000
      });
      
      const eventsData = response.data.data || [];
      
      // Get daily random events instead of just the latest 3
      const dailyEvents = getDailyRandomEvents(eventsData, 3);
      
      console.log('Daily random events:', dailyEvents);
      
      setEvents(dailyEvents);
      
    } catch (error) {
      console.error('Error fetching events:', error);
      setError("Failed to load today's events. Please try again later.");
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return "RECENTLY ADDED";
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return "TODAY";
      if (diffDays === 1) return "1 DAY AGO";
      return `${diffDays} DAYS AGO`;
    } catch (e) {
      return "RECENTLY";
    }
  };

  // Truncate title if too long
  const truncateTitle = (title, maxLength = 80) => {
    if (!title) return "Untitled Event";
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + "...";
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'HOME': 'FF7C7C',
      'INTERIORS': 'FF7C7C',
      'FOOD': 'F48C06',
      'DRINK': 'F48C06',
      'ENTERTAINMENT': '29B9E7',
      'TRAVEL': '5B72EE',
      'BUSINESS': 'F48C06',
    };
    
    for (const [key, color] of Object.entries(colors)) {
      if (category?.toUpperCase().includes(key)) {
        return color;
      }
    }
    return 'FF0077';
  };

  return (
    <>
      <Header/>
      
      <section className="w-full border shadow-sm max-w-7xl mx-auto rounded-xl px-4 sm:px-6 lg:px-8 py-2 md:py-8 ">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
            Today's Picks
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#FF7C7C] to-[#FF0077] mx-auto mt-3 rounded-full" />
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
            Discover upcoming events and activities to plan your next adventure
          </p>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner size="lg" color="primary" />
            <span className="ml-3 text-gray-500">Loading today's events...</span>
          </div>
        ) : error ? (
          // Error State
          <div className="text-center py-20">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-500">{error}</p>
            <button 
              onClick={fetchLatestEvents}
              className="mt-4 px-4 py-2 text-sm text-white bg-mainGradiant rounded-full hover:opacity-90"
            >
              Try Again
            </button>
          </div>
        ) : events.length === 0 ? (
          // Empty State
          <div className="text-center py-20">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500">No events available at the moment.</p>
            <p className="text-gray-400 text-sm mt-2">Check back later for new events!</p>
          </div>
        ) : (
          // Events Grid - Showing ACTUAL last added events from API
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {events.map((event) => {
                const categoryColor = getCategoryColor(event.category);
                const imageUrl = event.image || event.image_url || event.featured_image || 
                  `https://placehold.co/600x400/${categoryColor}/white?text=${encodeURIComponent(event.category || 'Event')}`;
                
                return (
                  <Card
                    key={event.id}
                    isPressable
                    as={Link}
                    href={`/blog/${event.slug || event.id}`}
                    className="group bg-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl overflow-hidden border-none"
                    style={{ textDecoration: 'none' }}
                  >
                    {/* Image Section */}
                    <div className="relative">
                      <Image
                        removeWrapper
                        alt={event.category || event.title}
                        className="w-full h-56  group-hover:scale-105 transition-transform duration-500"
                        src={imageUrl}
                      />
                      <div className="absolute top-4 left-4">
                        <span className="text-xs font-semibold tracking-wider text-white bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          {event.main_category || event.category || event.categories?.[0] || "EVENT"}
                        </span>
                      </div>
                    </div>

                    <CardBody className="p-5 md:p-6 space-y-3">
                      {/* Title */}
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 line-clamp-3 group-hover:text-[#FF0077] transition-colors">
                        {truncateTitle(event.title)}
                      </h3>

                      {/* Description/Excerpt */}
                      {event.excerpt && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {event.excerpt.substring(0, 100)}...
                        </p>
                      )}

                      {/* Author and Date */}
                      <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
                        <span className="font-medium">
                          {event.user_id ? "FROND" : "FROND"}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {formatDate(event.created_at || event.published_at)}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
            
            {/* View All Events Button */}
            {events.length > 0 && (
              <div className="text-center mt-12">
                <Link
                  href="/blog"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border-pink-500 border text-black rounded-full hover:opacity-90 transition-opacity"
                >
                  View All Events
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        )}
      </section>

      {/* <Goals/> */}
      <Dreams/>
      <Planing/>
      <Feedback/>
      <JoinFrond/>
    </>
  );
};

export default Homepage;